"use client";

import { MDXEditorMethods } from "@mdxeditor/editor";
import { isNil } from "lodash";
import { useLocale, useTranslations } from "next-intl";
import { FC, useEffect, useMemo, useRef, useState } from "react";

import { savePrivateNote } from "@/app/(main)/questions/actions";
import MarkdownEditor from "@/components/markdown_editor";
import LoadingSpinner from "@/components/ui/loading_spiner";
import RelativeTime from "@/components/ui/relative_time";
import SectionToggle from "@/components/ui/section_toggle";
import { useAuth } from "@/contexts/auth_context";
import { useDebouncedCallback } from "@/hooks/use_debounce";
import { Post } from "@/types/post";
import { formatDate } from "@/utils/formatters/date";

type Props = {
  post: Post;
};

const SavedAgo: FC<{ savedAt: Date }> = ({ savedAt }) => {
  const t = useTranslations();
  const locale = useLocale();

  const savedAtMs = savedAt.getTime();
  const [isJustNow, setIsJustNow] = useState(
    () => Date.now() - savedAtMs < 60_000
  );

  useEffect(() => {
    const elapsed = Date.now() - savedAtMs;
    const remaining = 60_000 - elapsed;

    if (remaining <= 0) {
      setIsJustNow(false);
      return;
    }

    setIsJustNow(true);
    const id = window.setTimeout(() => setIsJustNow(false), remaining);
    return () => window.clearTimeout(id);
  }, [savedAtMs]);

  return t.rich("savedAgo", {
    date: () =>
      isJustNow ? (
        t("justNow")
      ) : (
        <RelativeTime datetime={savedAt.toISOString()} format="relative">
          {formatDate(locale, savedAt)}
        </RelativeTime>
      ),
  });
};

const PrivateNote: FC<Props> = ({ post: { private_note, id } }) => {
  const t = useTranslations();
  const locale = useLocale();
  const { text, updated_at } = private_note || {};
  const [noteText, setNoteText] = useState(text || "");
  const [isLoading, setIsLoading] = useState(false);
  const [savedAt, setSavedAt] = useState<undefined | Date>();
  const { user } = useAuth();
  const editorRef = useRef<MDXEditorMethods>(null);

  const noteStatusDetails = useMemo(() => {
    if (isLoading) {
      return <LoadingSpinner size="1x" />;
    }

    if (savedAt) {
      return <SavedAgo savedAt={savedAt} />;
    }
  }, [savedAt, isLoading]);

  const saveNote = async (value: string) => {
    if (value === noteText) {
      return;
    }

    setNoteText(value);

    setIsLoading(true);
    await savePrivateNote(id, value);
    setIsLoading(false);

    setSavedAt(new Date());
  };

  const saveNoteDebounced = useDebouncedCallback(saveNote, 1500);

  if (!user) {
    return null;
  }

  return (
    <SectionToggle
      title={t("privateNote")}
      variant={text ? "orange" : "primary"}
      detailElement={(isOpen) => {
        if (isOpen) {
          return <div className="ml-auto text-xs">{noteStatusDetails}</div>;
        } else if (updated_at) {
          return (
            <div className="ml-auto text-xs">
              {t.rich("privateNoteUpdatedFrom", {
                date: () => (
                  <RelativeTime datetime={updated_at} format="relative">
                    {formatDate(locale, new Date(updated_at))}
                  </RelativeTime>
                ),
              })}
            </div>
          );
        }
      }}
    >
      <div className="bg-gray-0 dark:bg-gray-0-dark">
        <MarkdownEditor
          ref={editorRef}
          markdown={noteText}
          mode="write"
          onChange={(val) => {
            saveNoteDebounced(val);
          }}
          onBlur={() => {
            const val = editorRef.current?.getMarkdown();
            if (!isNil(val)) {
              saveNote(val);
            }
          }}
          withUgcLinks
          withCodeBlocks
        />
      </div>
    </SectionToggle>
  );
};

export default PrivateNote;
